import { useState } from 'react';
import FilterProvider from '@/context/filter/filter.context';
import { UserRoles } from '@/enums/enums';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import useAuth from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import LessonTable from '@/components/custom-tables/lesson.table';
import ContractViewSheet from '@/components/sheets/contract-sheet/contract-view-sheet';
import LessonViewSheet from '@/components/sheets/lesson-sheet/lesson-view-sheet';
import Tabs from '@/components/tabs/tabs-custom';

import RecurringLessonsTab from './-recurring-lessons-tab';

const homeRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view', 'bulk_update']).optional(),
  lesson: z.number().optional(),
  recurring_lesson: z.number().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  roomId: z.number().optional(),
  roomName: z.string().optional(),
});

export const Route = createFileRoute('/_app/(lessons)/lessons/')({
  validateSearch: zodSearchValidator(homeRouteSchema),
  component: Lessons,
});

function Lessons() {
  const { user } = useAuth();
  const { t } = useTranslation('tab');
  const [tab, setTab] = useState('lessons');

  const tabs = [
    {
      label: t('lessons'),
      value: 'lessons',
      content: (
        <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
          <LessonsTabHeader />
          <LessonsTable />
        </div>
      ),
    },
  ];

  if (user?.role === UserRoles.Root) {
    tabs.push({
      label: t('recurring_lessons'),
      value: 'recurring_lessons',
      content: (
        <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
          <RecurringLessonsTab />
        </div>
      ),
    });
  }
  return <Tabs tab={tab} onChangeTab={setTab} className='pt-4' tabs={tabs} />;
}

function LessonsTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['lesson', 'common']);

  const navigateToSettings = () => {
    navigate({ to: '/lessons/settings' });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={navigateToSettings} size='default'>
          <Settings className='h-6 w-6' />
          {t('settings', { ns: 'common' })}
        </Button>
      </div>

      <LessonViewSheet />
      <ContractViewSheet />
    </div>
  );
}

function LessonsTable() {
  return (
    <FilterProvider>
      <LessonTable />
    </FilterProvider>
  );
}
